// tab-navigation.service.ts
import {Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {PropostaExternaEnum} from '../../enumerable/poposta-externa.enum';

@Injectable({
  providedIn: 'root',
})
export class TabNavigationService {
  ehColaborador!: boolean;
  private declaracaoSaudeAtivada = new Subject<void>();
  declaracaoSaudeAtivada$ = this.declaracaoSaudeAtivada.asObservable();

  private documentosAtivada = new Subject<void>();
  documentosAtivada$ = this.documentosAtivada.asObservable();

  // BehaviorSubject para manter e emitir o estado atual da aba
  private activeTabSubject = new BehaviorSubject<string>('beneficiario');

  // Observable que os componentes podem assinar
  activeTab$ = this.activeTabSubject.asObservable();
  private abaLiberada = new BehaviorSubject<number>(1);
  abaLiberada$ = this.abaLiberada.asObservable();

  private currentStepSubject = new BehaviorSubject<number>(0);
  currentStep$ = this.currentStepSubject.asObservable();

  constructor() {}

  liberarProximaAba(numeroAba: number) {
    this.abaLiberada.next(numeroAba);
  }

  // Obtém a aba ativa atual
  obterAbaAtiva(): string {
    return this.activeTabSubject.getValue();
  }

  // Define uma nova aba ativa
  definirAbaAtiva(aba: string): void {
    this.activeTabSubject.next(aba);
  }

  // Define a aba ativa por número
  definirAbaAtivaPorNumero(numeroAba: number): boolean {
    if (!this.podeAcessarAba(numeroAba)) {
      return false;
    }

    let targetTab: string;
    switch (numeroAba) {
      case 0:
        targetTab = 'beneficiario';
        break;
      case 5:
        targetTab = 'declaracaoSaude';
        break;
      case 9:
        targetTab = 'documentos';
        break;
      case 10:
        targetTab = 'analise';
        break;
      case 12:
        targetTab = 'videoConferencia';
        break;
      case 14:
        targetTab = 'pagamento';
        break;
      case 15:
        targetTab = 'analisePagamento';
        break;
      case 16:
        targetTab = 'conclusao';
        break;
      default:
        targetTab = 'beneficiario';
    }

    this.definirAbaAtiva(targetTab);
    return true;
  }

  // Avança para a próxima aba
  avancarParaProximaAba(proximaAba?: any): void {
    if (proximaAba) {
      this.definirAbaAtiva(proximaAba);
      return;
    }
    const currentTab = this.obterAbaAtiva();
    switch (currentTab) {
      case 'beneficiario':
        this.definirAbaAtiva('declaracaoSaude');
        break;
      case 'declaracaoSaude':
        this.definirAbaAtiva('documentos');
        break;
      case 'documentos':
        this.definirAbaAtiva('analise');
        break;
      case 'analise':
        this.definirAbaAtiva('videoConferencia');
        break;
      case 'videoConferencia':
        this.definirAbaAtiva('pagamento');
        break;
      case 'pagamento':
      case 'analisePagamento':
        this.definirAbaAtiva('pagamento');
        break;
      case 'pagamentoConfirmado':
        this.definirAbaAtiva('beneficiario'); //enviar para operadora
        break;
    }
  }

  // Método para definir o step atual
  definirStepAtual(step: number): void {
    this.currentStepSubject.next(step);
  }

  // Método para obter o step atual
  obterStepAtual(): number {
    return this.currentStepSubject.getValue();
  }

  /**
   * Define se o usuário pode acessar a aba informada.
   */
  podeAcessarAba(numeroAba: number): boolean {
    const stepAtual = this.obterStepAtual();

    // 1. Regra principal:
    // Se o step atual >= 11, todas as abas anteriores ficam liberadas.porem deixar somente leitura
    if (!this.ehColaborador && this.liberarAbasInferiores(stepAtual, numeroAba)) {
      return true;
    }
    // 2. Aplica regras especiais específicas por step
    if (!this.podeAcessarAbaRegrasEspeciais(stepAtual, numeroAba)) {
      return false;
    }

    // 3. Regra geral: libera abas menores ao step atual
    if (numeroAba < stepAtual) {
      return true;
    }
    // 4. Regras normais de avanço de steps
    return this.regrasNormaisDeAvanco(stepAtual, numeroAba);
  }

  /* ===========================================================
   REGRA 1 — LIBERAR ABAS INFERIORES AUTOMATICAMENTE (>=11)
     Se step >= 11 libera qualquer aba anterior e segue o fluxo normal,de acesso a req no back
     EXEMPLOS:
      step 11 => libera 0..10
      step 12 => libera 0..11
      step 13 => libera 0..12
      step 14 => libera 0..13
   =========================================================== */
  private liberarAbasInferiores(stepAtual: number, numeroAba: number): boolean {
    if (stepAtual >= PropostaExternaEnum.RETORNO_DOCUMENTO) {
      return numeroAba < stepAtual - 2; //bloqueia até a aba de análise
    }
    return false; //deixa os forms soente leitura
  }

  //  REGRA 2 — REGRAS ESPECIAIS POR STEP
  private podeAcessarAbaRegrasEspeciais(stepAtual: number, numeroAba: number): boolean {
    switch (stepAtual) {
      /**
       * STEP 10 — ANALISE
       * ------------------------------------------------------------
       * O beneficiário mostra todas as abas anteriores a aba de analise.
       *
       * Colaborador mostra apenas a aba de documento.
       */
      case PropostaExternaEnum.ANALISE: // 10
        if (this.ehColaborador) {
          return numeroAba === PropostaExternaEnum.DOCUMENTO;
        }
        return (
          numeroAba === PropostaExternaEnum.CARTA_ORIENTACAO ||
          numeroAba === PropostaExternaEnum.DOCUMENTO ||
          numeroAba === PropostaExternaEnum.ANALISE
        );

      /**
       * STEP 11 — RETORNO_DOCUMENTO
       * ------------------------------------------------------------
       * O beneficiário precisa reenviar documentos.
       *
       * Permite acessar todas as abas anteriores.
       */
      case PropostaExternaEnum.RETORNO_DOCUMENTO: // 11
        return !(
          numeroAba === PropostaExternaEnum.ANALISE ||
          numeroAba === PropostaExternaEnum.VIDEO_CONFERENCIA ||
          numeroAba >= PropostaExternaEnum.PAGAMENTO
        );

      /**
       * STEP 12 — VIDEO_CONFERENCIA
       * ------------------------------------------------------------
       * O beneficiário já passou pela análise e aguarda videoconferência.
       *
       * Abas iniciais continuam liberadas porem os forms ficam soente leitura.
       */
      case PropostaExternaEnum.VIDEO_CONFERENCIA: // 12
        return !(
          numeroAba === PropostaExternaEnum.DOCUMENTO ||
          numeroAba === PropostaExternaEnum.ANALISE ||
          numeroAba >= PropostaExternaEnum.PAGAMENTO
        );

      /**
       * STEP 13 — RETORNO_DS (Retorno Declaração de Saúde)
       * ------------------------------------------------------------
       * O beneficiário precisa corrigir dados da declaração de saúde.
       *
        O Colaborador não ativa nenhuma aba, pois após o beneficiário concluir o próximo passo é pagamento
       */
      case PropostaExternaEnum.RETORNO_DS: // 13
        if (this.ehColaborador) {
          return numeroAba >= PropostaExternaEnum.CONCLUSAO;
        }
        return !(
          numeroAba === PropostaExternaEnum.DOCUMENTO ||
          numeroAba === PropostaExternaEnum.ANALISE ||
          numeroAba === PropostaExternaEnum.VIDEO_CONFERENCIA ||
          numeroAba >= PropostaExternaEnum.PAGAMENTO
        );

      /**
       * STEP 9 — DOCUMENTO
       * ------------------------------------------------------------
       * O usuário está enviando documentos.
       *
       * BLOQUEIA:
       *  - A partir de ANÁLISE (>= 10)
       */
      case PropostaExternaEnum.DOCUMENTO: // 9
        return numeroAba < PropostaExternaEnum.ANALISE;

      case PropostaExternaEnum.ANALISE_PAGAMENTO: //15
        if (this.ehColaborador) {
          return numeroAba >= PropostaExternaEnum.PAGAMENTO;
        }
        return true;

      case PropostaExternaEnum.PAGAMENTO_CONFIRMADO: //24
        if (this.ehColaborador) {
          return numeroAba <= PropostaExternaEnum.PAGAMENTO_CONFIRMADO;
        }
        return true;

      /**
       * ------------------------------------------------------------
       * Outros steps seguem o fluxo normal de regras gerais.
       */
      default:
        if (stepAtual >= PropostaExternaEnum.PAGAMENTO_CONFIRMADO && this.ehColaborador) {
          // colaborador NÃO pode acessar abas após a de beneficiario
          return numeroAba < PropostaExternaEnum.CARTA_ORIENTACAO;
        }

        return true;
    }
  }

  //  REGRA 3 — AVANÇOS NORMAIS ENTRE ABAS
  private regrasNormaisDeAvanco(stepAtual: number, numeroAba: number): boolean {
    switch (numeroAba) {
      case PropostaExternaEnum.CARTA_ORIENTACAO:
        return stepAtual > PropostaExternaEnum.BENEFICIARIO_BANCO;

      case PropostaExternaEnum.DOCUMENTO:
        return stepAtual > PropostaExternaEnum.QUESTIONARIO;

      case PropostaExternaEnum.ANALISE:
        return stepAtual > PropostaExternaEnum.DOCUMENTO;

      case PropostaExternaEnum.VIDEO_CONFERENCIA:
        return stepAtual > PropostaExternaEnum.ANALISE;

      case PropostaExternaEnum.PAGAMENTO:
        return stepAtual > PropostaExternaEnum.VIDEO_CONFERENCIA;

      case PropostaExternaEnum.CONCLUSAO:
        return stepAtual >= PropostaExternaEnum.CONCLUSAO;

      default:
        return true;
    }
  }

  public notificarDeclaracaoSaudeAtivada() {
    this.declaracaoSaudeAtivada.next();
  }

  public notificarDocumentosAtivada() {
    this.documentosAtivada.next();
  }
}
